/*
 * @Author: liuxw@codoon.com
 * @Date: 2020-10-29 19:13:50
 * @Last Modified by: liuxw@codoon.com
 * @Last Modified time: 2021-07-12 10:50:05
 * @Content:
 */

import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import day from "../../../../utils/day";
import "./index.css";
import {getGoalState} from "../../../utils";
import {IGoalCarouselItemState} from "../GoalCarousel/GoalCarouselItem/interface";
import classnames from "classnames";
import {Dayjs} from "dayjs";
import {TIME_FORMATTER} from "../../../../constants/time";
import { IGoal } from "../../../../service/activity/interface";

const PREFIX = "GoalDetail";

interface IProps {
  goal: IGoal;
  state: IGoalCarouselItemState;
  currTime: Dayjs;
}

const GoalDetail: FC<IProps> = memo(({goal, state, currTime}) => {

  // 计算剩余时间
  const initRestMs = useCallback((goal, currTime) => {
    const endTime = day(goal.limit_end, TIME_FORMATTER);
    return Math.max(0, endTime.valueOf() - currTime.valueOf());
  }, []);

  const timer = useRef<null | number>(null);
  const [restMs, setRestMs] = useState<number>(initRestMs(goal, currTime));

  useEffect(() => {
    if (timer.current) {
      window.clearInterval(timer.current);
    }

    // 当参数变化时，重新初始化一次剩余时间
    const nextRestMs = initRestMs(goal, currTime);
    setRestMs(nextRestMs);

    // 如果有剩余时间，则注册5秒计算一次剩余时间
    if (nextRestMs > 0) {
      timer.current = window.setInterval(() => {
        const currTime = day();
        const nextRestMs = initRestMs(goal, currTime);
        setRestMs(nextRestMs);
        // 当没有剩余时间时，就可以清除定时器了
        if (nextRestMs <= 0 && timer.current) {
          window.clearInterval(timer.current);
        }
      }, 5000);
    }

    // 卸载时清除定时器
    return () => {
      if (timer.current) {
        window.clearInterval(timer.current);
      }
    };
  }, [goal, currTime]);

  const fillZero = useCallback((num: number, fillZero?: boolean) => {
    let filledStr = `${num}`;
    // 时、分、秒需要补0
    if (fillZero) {
      filledStr = `${num < 10 ? 0 : ""}${num}`;
    }
    return filledStr
      .split("")
      .map((letter) => <span className={`${PREFIX}-letter`}>{letter}</span>);
  }, []);

  // 渲染头部
  const renderHeader = useCallback(() => {
    if (state == 1) {
      let days = 0;
      let hours = 0;
      let minutes = 0;

      if (restMs > 0) {
        const restDuration = day.duration(restMs);
        days = Math.floor(restDuration.asDays());
        hours = restDuration.hours();
        minutes = restDuration.minutes();
      }

      // 进行中，现实倒计时
      return (
        <div
          className={classnames(`${PREFIX}-headerNormalText`, "is-inProgress")}
        >
          {days > 0 ? (
            <>
              <span>剩余</span>
              <span className={`${PREFIX}-headerNumText`}>
                {fillZero(days)}
              </span>
              <span>天</span>
              <span className={`${PREFIX}-headerNumText`}>
                {fillZero(hours)}
              </span>
              <span>时</span>
            </>
          ) : (
            <>
              <span>剩余</span>
              <span className={`${PREFIX}-headerNumText`}>
                {fillZero(hours)}
              </span>
              <span>时</span>
              <span className={`${PREFIX}-headerNumText`}>
                {fillZero(minutes)}
              </span>
              <span>分</span>
            </>
          )}
        </div>
      );
    } else if (state == 2) {
      // 已完成
      return (
        <div
          className={classnames(`${PREFIX}-headerNormalText`, "is-completed")}
        >
          <span className={`${PREFIX}-headerGreenText`}>恭喜你，</span>
          <span>完成了该目标</span>
        </div>
      );
    } else if (state == 3) {
      // 已结束，未完成
      return (
        <div
          className={classnames(`${PREFIX}-headerNormalText`, "is-finished")}
        >
          <span className={`${PREFIX}-headerRedText`}>加油，</span>
          <span>下次努力完成目标</span>
        </div>
      );
    } else {
      // 未开始
      return (
        <div
          className={classnames(`${PREFIX}-headerNormalText`, "is-notStart")}
        >
          {`${goal.limit_start} 开始挑战`}
        </div>
      );
    }
  }, [goal, state, currTime, restMs]);

  /**
   * 获取进度值，如 x次、xkm
   */
  const getProgressValue = useCallback(
    (val: number, valType: IGoal["val_type"]) => {
      if (valType == 1) {
        // 距离
        return `${val || 0}km`;
      } else if (valType == 2) {
        // 时间
        const duration = day.duration(val * 1000);
        const hours = duration.hours() || 0;
        const minutes = duration.minutes() || 0;
        const seconds = duration.seconds() || 0;
        return `${hours ? `${hours}h` : ""} ${
          minutes ? `${minutes}m` : ""
        } ${seconds}s`;
      } else if (valType == 3) {
        // 卡路里
        return `${val || 0}kcal`;
      } else if (valType == 4) {
        // 次数
        return `${val || 0}次`;
      } else if (valType == 5) {
        // 连续打卡
        return `${val || 0}天`;
      } else if (valType == 6) {
        // 累计打卡
        return `${val || 0}天`;
      }
      return "";
    },
    []
  );

  const currProgress = useMemo(() => {
    return getProgressValue(goal.cur_val, goal.val_type);
  }, [goal, getProgressValue]);

  const totalProgress = useMemo(() => {
    return getProgressValue(goal.goal_val, goal.val_type);
  }, [goal, getProgressValue]);

  return (
    <div className={PREFIX}>
      <div className={`${PREFIX}-header`}>{renderHeader()}</div>
      <div className={`${PREFIX}-info`}>
        <div
          className={`${PREFIX}-infoCompleted`}
        >{`你已完成 ${currProgress}`}</div>
        <div className={`${PREFIX}-infoTotal`}>
          目标
          <span
            className={`${PREFIX}-infoTotalValue`}
          >{` ${totalProgress}`}</span>
        </div>
      </div>
      <div className={`${PREFIX}-progress`}>
        <div
          className={`${PREFIX}-progressContent`}
          style={{width: `${goal.progress || 0}%`}}
        />
      </div>
    </div>
  );
});

export default GoalDetail;
